import React, { useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { observer } from "mobx-react";
import QuantityControls from "./QuantityControls";
import VariationToggle from './VariationToggle';
import VariationSelect from './VariationSelect';
import ExternalLink from "./ExternalLink";

/**
 * Helper function to determine the variant type based on controller
 */
const getVariantType = (controller) => {
  switch (controller) {
    case "internal": return "internal";
    case "modal": return "modal";
    case "cart": return "cart";
    default: return "v2";
  }
};

const AddToCart = observer(({
  handleRemoveFromCart,
  handleAddToCart,
  handleUpdateQuantity,
  quantity,
  productWeightQuantity,
  quantityController = "default",
  handleVariationChange,
  productType,
  selectedVariation,
  product,
  productDisplayType,
  setSelectedVariation,
  layout = "vertical", // 'vertical' or 'horizontal'
  isModal = false,
}) => {
  const t = useTranslations('Product');

  // Memoize published variations to avoid recalculating on every render
  const publishedVariations = useMemo(() => {
    return product?.variations?.filter(variation => variation.status === "publish") || [];
  }, [product?.variations]);

  useEffect(() => {
    if (productType === "variable" && !selectedVariation && publishedVariations.length > 0) {
      setSelectedVariation(publishedVariations[0]); // Set the first published variation by default
    }
  }, [productType, publishedVariations, selectedVariation, setSelectedVariation]);

  // Classes to manage vertical or horizontal layout
  const layoutClass = layout === 'horizontal' ? 'flex items-start space-x-4' :
    'space-y-1 rtl:space-y-reverse';

  return (
    <div className={layoutClass}>
      {(productType === "variable" && selectedVariation) || productType === "simple" ? (
        <QuantityControls
          handleRemoveFromCart={handleRemoveFromCart}
          handleAddToCart={handleAddToCart}
          handleUpdateQuantity={handleUpdateQuantity}
          quantity={quantity}
          productWeightQuantity={productWeightQuantity}
          productDisplayType={productDisplayType}
          product={product}
          variant={getVariantType(quantityController)}
          disableInput
        />
      ) : null}

      {productType === "variable" && publishedVariations.length === 2 && (
        <VariationToggle
          product={product}
          selectedVariation={selectedVariation}
          setSelectedVariation={setSelectedVariation}
          t={t}
          layout={(layout === "horizontal" && "compact")}
          isModal={isModal}
        />
      )}

      {productType === "variable" && publishedVariations.length > 2 && (
        <VariationSelect
          product={{ ...product, variations: publishedVariations }} // Pass filtered variations
          selectedVariation={selectedVariation}
          handleVariationChange={handleVariationChange}
          t={t}
          layout={(layout === "horizontal" && "compact")}
        />
      )}

      {productType === "external" && (
        <ExternalLink
          url={product?.external_url ?? ""}
          text={product?.button_text ?? ""}
        />
      )}
    </div>
  );
});

export default AddToCart;
